import React from 'react';
import classNames from 'classnames';
import SinglePhoneInput from './SinglePhoneInput';
import styles from './OwnPhoneInput.module.scss';

const BACKSPACE = 8;
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;
const DELETE = 46;
const SPACEBAR = 32;

const OwnPhoneInput = ({
  value = ['', '', '', '', '', '', ''],
  onChange = () => null,
  separationPoints = [2, 4],
  placeholder = 'X',
  inputClass,
  className,
}) => {
  const [activeInput, setActiveInput] = React.useState(0);

  const validateSeparationPoints = separationPoints.map(Number);

  const isInputValueValid = (currentValue) => {
    const isTypeValid = !Number.isNaN(parseInt(currentValue, 10));
    return isTypeValid && currentValue.trim().length === 1;
  };

  const focusInput = (input) => {
    const active = Math.max(Math.min(value.length - 1, input), 0);
    setActiveInput(active);
  };

  const handlePhoneChange = (phone) => {
    onChange(phone);
  };

  const changePhoneAtFocus = (inputValue) => {
    const instance = [...value];
    instance[activeInput] = inputValue;
    handlePhoneChange(instance);
  };

  const handleOnChange = (event) => {
    const { value } = event.target;
    if (isInputValueValid(value)) changePhoneAtFocus(value);
  };

  // Focus on next input
  const focusNextInput = () => {
    focusInput(activeInput + 1);
  };

  // Focus on previous input
  const focusPrevInput = () => {
    focusInput(activeInput - 1);
  };

  const handleOnInput = (event) => {
    if (isInputValueValid(event.target.value)) focusNextInput();
  };

  const handleOnKeyDown = (event) => {
    if (event.keyCode === BACKSPACE || event.key === 'Backspace') {
      event.preventDefault();
      changePhoneAtFocus('');
      focusPrevInput();
    } else if (event.keyCode === DELETE || event.key === 'Delete') {
      event.preventDefault();
      changePhoneAtFocus('');
    } else if (event.keyCode === LEFT_ARROW || event.key === 'ArrowLeft') {
      event.preventDefault();
      focusPrevInput();
    } else if (event.keyCode === RIGHT_ARROW || event.key === 'ArrowRight') {
      event.preventDefault();
      focusNextInput();
    } else if (
      event.keyCode === SPACEBAR ||
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      event.key === 'Space' ||
      event.key === 'e' ||
      event.key === 'E' ||
      event.key === '+' ||
      event.key === ',' ||
      event.key === '.' ||
      event.key === '-'
    ) {
      event.preventDefault();
    }
  };

  const handleOnPaste = (event) => {
    event.preventDefault();
    let nextActiveInput = activeInput;
    const pastedData = event.clipboardData
      .getData('text/plain')
      .slice(0, value.length - activeInput)
      .split('');
    for (let pos = 0; pos < value.length; ++pos) {
      if (pos >= activeInput && pastedData.length > 0) {
        value[pos] = pastedData.shift();
        nextActiveInput++;
      }
    }
    setActiveInput(nextActiveInput);
    focusInput(nextActiveInput);
    handlePhoneChange(value);
  };

  const renderInputs = (item, index) => {
    const showSeparator = validateSeparationPoints.includes(index);
    return (
      <React.Fragment key={index}>
        <SinglePhoneInput
          data-testid={`single-input-${index}`}
          inputClass={inputClass}
          placeholder={placeholder}
          focus={activeInput === index}
          value={item}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          onInput={handleOnInput}
          onPaste={handleOnPaste}
          onFocus={(event) => {
            setActiveInput(index);
            event.target.select();
          }}
          onBlur={() => setActiveInput(-1)}
        />
        {showSeparator && <div className={styles.separator}>-</div>}
      </React.Fragment>
    );
  };

  return (
    <div
      className={classNames([styles.container, className])}
      itemScope
      itemType='https://schema.org/Thing'
    >
      {value.map(renderInputs)}
    </div>
  );
};

export default OwnPhoneInput;
